import { type DraftRule, DraftRuleId } from '@/rules-management/domain/DraftRule.ts';

const publishableOf = (drafts: DraftRule[]): DraftRule[] =>
  drafts.filter((draft) => draft.isPublishable);

export class DraftRulesSelection {
  readonly #ids: ReadonlySet<string>;

  private constructor(ids: ReadonlySet<string>) {
    this.#ids = ids;
  }

  static empty(): DraftRulesSelection {
    return new DraftRulesSelection(new Set());
  }

  toggle(draft: DraftRule): DraftRulesSelection {
    if (!draft.isPublishable) {
      return this;
    }
    return this.holds(draft) ? this.deselectAll([draft]) : this.selectAll([draft]);
  }

  selectAll(drafts: DraftRule[]): DraftRulesSelection {
    const ids = new Set(this.#ids);
    publishableOf(drafts).forEach((draft) => ids.add(draft.id.value));
    return new DraftRulesSelection(ids);
  }

  deselectAll(drafts: DraftRule[]): DraftRulesSelection {
    const ids = new Set(this.#ids);
    drafts.forEach((draft) => ids.delete(draft.id.value));
    return new DraftRulesSelection(ids);
  }

  retain(ids: DraftRuleId[]): DraftRulesSelection {
    const retained = new Set(ids.map((id) => id.value));
    return new DraftRulesSelection(new Set([...this.#ids].filter((id) => retained.has(id))));
  }

  holds(draft: DraftRule): boolean {
    return this.#ids.has(draft.id.value);
  }

  holdsAllOf(drafts: DraftRule[]): boolean {
    const publishable = publishableOf(drafts);
    return publishable.length > 0 && publishable.every((draft) => this.holds(draft));
  }

  holdsPartOf(drafts: DraftRule[]): boolean {
    const publishable = publishableOf(drafts);
    return publishable.some((draft) => this.holds(draft)) && !this.holdsAllOf(drafts);
  }

  get ids(): DraftRuleId[] {
    return [...this.#ids].map((id) => DraftRuleId.of(id));
  }

  get count(): number {
    return this.#ids.size;
  }

  get isEmpty(): boolean {
    return this.count === 0;
  }
}
